import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModalToggle } from '@make-software/tg-mini-app-ui';

import {
  setError,
  selectGiveawayError,
  selectGiveawayLoading,
  selectGiveawayData,
  fetchGiveawayData,
  AppDispatch,
  claimSubname,
  searchCSPRSubname,
  useMakeSubnamePrimary,
} from '@/shared';
import { TxStatus } from '@/entities';
import { getErrorMessage, isValidSubname } from '@/utils';
import { CLAIM_TIMEOUT_MS, UNAVAILABLE_MESSAGE, VALIDATION_ERROR_MESSAGE } from '@/constants';

export const useClaimGift = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [subname, setSubname] = useState('');
  const [isNameAvailable, setIsNameAvailable] = useState(false);
  const { open: isClaimModalOpen, openModal: openClaimModal, closeModal: closeClaimModal } = useModalToggle('claim-modal');
  const [claimStatus, setClaimStatus] = useState<TxStatus>('processing');

  const wsRef = useRef<WebSocket | null>(null);
  const claimTimeoutRef = useRef<number | null>(null);

  const searchError = useSelector(selectGiveawayError);
  const isLoading = useSelector(selectGiveawayLoading);
  const giveawayData = useSelector(selectGiveawayData);

  const assignedSubname = giveawayData?.assigned_subname;
  const meetsCriteria = giveawayData?.meets_criteria;

  useEffect(() => {
    if (assignedSubname?.label) {
      setSubname(assignedSubname.label);
    }
  }, [assignedSubname]);

  const hasIncompleteCriteria = useMemo(() => {
    if (!meetsCriteria) return true;
    return Object.values(meetsCriteria).some((value) => value !== true);
  }, [meetsCriteria]);

  const isSubnameValid = useMemo(() => isValidSubname(subname), [subname]);

  const hasValidationError = useMemo(
    () => subname.length > 0 && !isSubnameValid,
    [subname, isSubnameValid]
  );

  const hasAssignedSubname = Boolean(assignedSubname?.label);
  const isPrimary = Boolean(assignedSubname?.primary);

  const {
    isMakePrimaryModalOpen,
    setIsMakePrimaryModalOpen,
    makePrimaryStatus,
    submitting,
    onMakeSubnamePrimary
  } = useMakeSubnamePrimary(subname);

  const clearClaimTimeout = useCallback(() => {
    if (claimTimeoutRef.current) {
      window.clearTimeout(claimTimeoutRef.current);
      claimTimeoutRef.current = null;
    }
  }, []);

  const closeWs = useCallback(
    (code = 1000, reason = 'close') => {
      clearClaimTimeout();

      try {
        wsRef.current?.close(code, reason);
      } catch {
        // ignore
      } finally {
        wsRef.current = null;
      }
    },
    [clearClaimTimeout]
  );

  useEffect(() => {
    return () => closeWs(1000, 'unmount');
  }, [closeWs]);

  const handleSubnameChange = useCallback(
    (value: string) => {
      setSubname(value);
      setIsNameAvailable((prev) => (prev ? false : prev));

      const isValid = isValidSubname(value);

      if ((isValid && hasValidationError) || searchError) {
        dispatch(setError(null));
      }

      if (!isValid && value.length > 0) {
        dispatch(setError(VALIDATION_ERROR_MESSAGE));
      }
    },
    [dispatch, hasValidationError, searchError]
  );

  const handleSearchSubname = useCallback(async () => {
    if (!isSubnameValid) {
      dispatch(setError('Subname must be at least a 3 character.'));
      return;
    }

    try {
      const resp = await searchCSPRSubname(subname);
      setIsNameAvailable(resp.data?.available ?? false);

      if (!resp.data?.available) {
        dispatch(setError(UNAVAILABLE_MESSAGE));
        return;
      }

      if (searchError) {
        dispatch(setError(null));
      }
    } catch (e: unknown) {
      setIsNameAvailable(false);
      dispatch(setError(getErrorMessage(e, UNAVAILABLE_MESSAGE)));
    }
  }, [dispatch, isSubnameValid, subname, searchError]);

  const onGetSubnameClick = useCallback(async () => {
    if (!subname) return;

    if (!isSubnameValid) {
      dispatch(setError('Subname must be at least a 3 character.'));
      return;
    }

    openClaimModal();
    dispatch(setError(null));
    setClaimStatus('processing');

    closeWs(1000, 'restart');

    let claimResp: any;
    try {
      claimResp = await claimSubname(subname);
    } catch (e: unknown) {
      dispatch(setError(getErrorMessage(e, 'Failed to claim subname')));
      setClaimStatus('error');
      return;
    }

    const txHash = claimResp?.data?.tx_hash;
    if (!txHash) {
      dispatch(setError('Missing tx_hash in response'));
      setClaimStatus('error');
      return;
    }

    const proxy = window.csprclick?.getCsprCloudProxy();
    if (!proxy?.newWebSocket) {
      dispatch(setError('CSPR.cloud proxy is not available'));
      setClaimStatus('error');
      return;
    }

    const ws = proxy.newWebSocket(`/transactions/${txHash}`);
    wsRef.current = ws;

    clearClaimTimeout();
    claimTimeoutRef.current = window.setTimeout(() => {
      dispatch(setError('Transaction is taking too long. Please try again.'));
      setClaimStatus('error');
      closeWs(1000, 'timeout');
    }, CLAIM_TIMEOUT_MS);

    ws.onopen = () => {
      setClaimStatus((prev) => (prev === 'processing' ? prev : 'processing'));
    };

    ws.onmessage = (event: MessageEvent) => {
      const raw = event?.data;

      let parsed: any = null;
      try {
        parsed = JSON.parse(raw);
      } catch {
        return;
      }

      const action = parsed?.action;

      if (action === 'created') {
        setClaimStatus('processed');
        closeWs(1000, 'created');
        return;
      }

      if (action === 'rejected' || action === 'error') {
        dispatch(setError(parsed?.message ?? UNAVAILABLE_MESSAGE));
        setClaimStatus('error');
        closeWs(1000, 'error');
        return;
      }
    };

    ws.onerror = (e: Event | ErrorEvent) => {
      dispatch(setError(getErrorMessage(e, 'WebSocket error')));
      setClaimStatus('error');
      closeWs(1006, 'ws error');
    };

    ws.onclose = () => {
      wsRef.current = null;
      clearClaimTimeout();
    };
  }, [subname, isSubnameValid, dispatch, closeWs, clearClaimTimeout]);

  const isInputDisabled = useMemo(
    () => !!assignedSubname?.label || hasIncompleteCriteria || submitting,
    [assignedSubname?.label, hasIncompleteCriteria, submitting]
  );

  const isSearchDisabled = useMemo(
    () =>
      isLoading ||
      !isSubnameValid ||
      Boolean(searchError) ||
      hasIncompleteCriteria ||
      submitting ||
      hasAssignedSubname,
    [isLoading, isSubnameValid, searchError, hasIncompleteCriteria, submitting, hasAssignedSubname]
  );

  const onModalClose = (modalType: 'claim' | 'makePrimary') => () => {
    if (modalType === 'claim') {
      closeClaimModal();
    } else {
      setIsMakePrimaryModalOpen(false);
      dispatch(fetchGiveawayData());
    }

    dispatch(setError(null));
  };

  return {
    searchError,
    assignedSubname,
    hasIncompleteCriteria,
    isInputDisabled,
    isSearchDisabled,
    isNameAvailable,
    hasAssignedSubname,
    isPrimary,

    isClaimModalOpen,
    claimStatus,
    onMakeSubnamePrimary,
    isMakePrimaryModalOpen,
    makePrimaryStatus,
    onModalClose,
    handleSubnameChange,
    handleSearchSubname,
    onGetSubnameClick,
    submitting,
    subname
  };
};
